import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PricingPage.css';

// ── Data ──────────────────────────────────────────────────────────────────────

const TIERS = [
  {
    id: 'community',
    name: 'Community',
    monthly: 0,
    tagline: 'Self-hosted · Open source · MIT licentie',
    cta: 'Download gratis',
    ctaStyle: 'outline',
    features: [
      { text: 'Tot 5 repositories' },
      { text: 'Vulnerability scanning (OSV)' },
      { text: 'SBOM upload & diff' },
      { text: 'Licentie-analyse' },
      { text: 'REST API' },
      { text: 'Community support (GitHub)' },
      { text: 'CI/CD wizard', no: true },
      { text: 'PDF rapporten', no: true },
      { text: 'Webhook alerts', no: true },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    monthly: 49,
    yearly: 39,
    tagline: 'SaaS · Per organisatie · Klein team',
    cta: '14 dagen gratis proberen',
    ctaStyle: 'outline',
    features: [
      { text: 'Tot 25 repositories' },
      { text: 'Vulnerability scanning (OSV)' },
      { text: 'SBOM upload & diff' },
      { text: 'Licentie-analyse' },
      { text: 'PDF rapporten' },
      { text: 'CI/CD wizard (GitHub Actions)' },
      { text: 'Webhook alerts (Slack, Teams)' },
      { text: 'AI assistent' },
      { text: 'RBAC (Admin / Analyst / Viewer)', no: true },
      { text: 'CRA / NIST / ISO 27001 mapping', no: true },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    monthly: 249,
    yearly: 199,
    featured: true,
    tagline: 'SaaS · Meest gekozen · Mid-size teams',
    cta: '14 dagen gratis proberen',
    ctaStyle: 'primary',
    features: [
      { text: 'Unlimited repositories' },
      { text: 'Alles uit Starter' },
      { text: 'RBAC: Admin / Analyst / Viewer' },
      { text: 'CRA / NIST / ISO 27001 mapping' },
      { text: 'VEX-statements' },
      { text: 'Audit log (90 dagen)' },
      { text: 'Jira remediation sync (v1)' },
      { text: 'SLA 99.9%' },
      { text: 'Priority e-mail support' },
      { text: 'SSO / SAML', no: true },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: null,
    tagline: 'On-premise of SaaS · Custom contract',
    cta: 'Neem contact op',
    ctaStyle: 'ghost',
    features: [
      { text: 'Alles uit Professional' },
      { text: 'On-premise deployment' },
      { text: 'SSO / SAML (Okta, Azure AD)' },
      { text: 'Onbeperkte audit log retentie' },
      { text: 'Custom policies & thresholds' },
      { text: 'PostgreSQL backend' },
      { text: 'Dedicated Customer Success Manager' },
      { text: 'SLA 99.99%' },
      { text: 'Pentest rapport op aanvraag' },
      { text: 'NDA & DPA beschikbaar' },
    ],
  },
];

const COMPARE_ROWS = [
  { section: 'Repositories & SBOM' },
  { label: 'Aantal repositories', community: '5', starter: '25', pro: 'Unlimited', ent: 'Unlimited' },
  { label: 'SBOM formaten (SPDX, CycloneDX)', community: true, starter: true, pro: true, ent: true },
  { label: 'SBOM diff & versiehistorie', community: true, starter: true, pro: true, ent: true },
  { label: 'Bulk SBOM upload via API', community: true, starter: true, pro: true, ent: true },

  { section: 'Vulnerability & Licentie' },
  { label: 'OSV vulnerability scan', community: true, starter: true, pro: true, ent: true },
  { label: 'CVSS score & FixedIn info', community: true, starter: true, pro: true, ent: true },
  { label: 'VEX statements', community: false, starter: false, pro: true, ent: true },
  { label: 'Remediation tracking', community: true, starter: true, pro: true, ent: true },
  { label: 'Licentie-risicocategorieën', community: true, starter: true, pro: true, ent: true },

  { section: 'Compliance & Rapportage' },
  { label: 'PDF / CSV export', community: false, starter: true, pro: true, ent: true },
  { label: 'NTIA compliance check', community: true, starter: true, pro: true, ent: true },
  { label: 'CRA / NIST / ISO 27001 mapping', community: false, starter: false, pro: true, ent: true },
  { label: 'Audit log', community: false, starter: false, pro: '90 dagen', ent: 'Onbeperkt' },

  { section: 'Integraties & Automatisering' },
  { label: 'CI/CD GitHub Actions wizard', community: false, starter: true, pro: true, ent: true },
  { label: 'Webhook alerts (Slack, Teams)', community: false, starter: true, pro: true, ent: true },
  { label: 'Jira remediation sync (v1)', community: false, starter: false, pro: true, ent: true },
  { label: 'ServiceNow integratie (roadmap)', community: false, starter: false, pro: false, ent: true },
  { label: 'SSO / SAML (Okta, Azure AD)', community: false, starter: false, pro: false, ent: true },

  { section: 'Platform & Support' },
  { label: 'RBAC (rollen & rechten)', community: false, starter: false, pro: true, ent: true },
  { label: 'AI assistent', community: false, starter: true, pro: true, ent: true },
  { label: 'On-premise deployment', community: true, starter: false, pro: false, ent: true },
  { label: 'SLA', community: false, starter: false, pro: '99.9%', ent: '99.99%' },
  { label: 'Support', community: 'Community', starter: 'E-mail', pro: 'Priority e-mail', ent: 'Dedicated CSM' },
];

const FAQ = [
  {
    q: 'Kan ik DepGuard gratis uitproberen?',
    a: 'Ja. De Community editie is volledig gratis en open source (MIT). Voor Starter en Professional bieden we een 14-dagen trial aan zonder creditcard.',
  },
  {
    q: 'Wat is het verschil tussen Community en SaaS-tiers?',
    a: 'Community is self-hosted — jij beheert de installatie, updates en database. SaaS-tiers worden door DepGuard gehost met automatische updates, backups en SLA-garanties.',
  },
  {
    q: 'Hoe werkt de EU Cyber Resilience Act (CRA) compliance mapping?',
    a: 'DepGuard mapt elke bevinding automatisch naar de relevante CRA-artikelen (o.a. Art. 13 vulnerability handling). De mapping is beschikbaar als exporteerbaar rapport in Professional en Enterprise.',
  },
  {
    q: 'Kan ik upgraden of downgraden?',
    a: 'Ja, je kunt op elk moment upgraden. Bij downgrade blijf je actief tot het einde van je betaalperiode en word je daarna gemigreerd naar de lagere tier.',
  },
  {
    q: 'Zijn er kortingen voor startups of non-profits?',
    a: 'Ja. We bieden 50% korting voor startups (<3 jaar, <10 fte) en non-profit organisaties. Neem contact op via het contactformulier.',
  },
  {
    q: 'Wat is inbegrepen in het early adopter aanbod?',
    a: 'De eerste 10 klanten ontvangen Professional voor €99/maand lifetime — ook als de prijs later stijgt. Dit is onze dankbetuiging voor vroege feedback en vertrouwen.',
  },
  {
    q: 'Hoe zit het met GDPR en databeveiliging?',
    a: 'DepGuard SaaS draait op EU-servers (Frankfurt). We verwerken SBOM-data en metadata, maar nooit de broncode zelf. Een DPA is beschikbaar voor Enterprise klanten.',
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function PricingPage() {
  const [yearly, setYearly] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  function handleCta(tier) {
    if (tier.id === 'community') {
      window.open('https://github.com/depguard/depguard', '_blank');
    } else if (tier.id === 'enterprise') {
      navigate('/contact?type=enterprise');
    } else {
      navigate(`/trial?plan=${tier.id}`);
    }
  }

  function price(tier) {
    if (tier.monthly === null) return 'Op aanvraag';
    if (tier.monthly === 0) return 'Gratis';
    const amt = yearly && tier.yearly ? tier.yearly : tier.monthly;
    return `€${amt}`;
  }

  return (
    <div className="page-content pricing-page">

      {/* ── Hero ── */}
      <div className="pricing-hero">
        <div className="pricing-hero-eyebrow">Transparante prijzen · Geen verrassingen</div>
        <h1>De juiste keuze voor<br />elk team</h1>
        <p className="pricing-hero-sub">
          Van open source community tot enterprise on-premise — DepGuard schaalt mee met jouw organisatie.
        </p>

        {/* Billing toggle */}
        <div>
          <div className="pricing-toggle">
            <button
              className={`pricing-toggle-btn${!yearly ? ' active' : ''}`}
              onClick={() => setYearly(false)}
            >Maandelijks</button>
            <button
              className={`pricing-toggle-btn${yearly ? ' active' : ''}`}
              onClick={() => setYearly(true)}
            >
              Jaarlijks
              <span className="pricing-discount-pill">−20%</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Early adopter banner ── */}
      <div className="pricing-early-banner">
        <div className="pricing-early-icon">🚀</div>
        <div className="pricing-early-text">
          <strong>Early Adopter Deal — eerste 10 klanten</strong>
          <span>Professional voor <strong>€99/maand lifetime</strong> · Sluit aan voor de officiële SaaS launch en vergrendel je prijs voor altijd.</span>
        </div>
        <button className="btn-pricing-primary" style={{ width: 'auto', padding: '9px 20px' }}
          onClick={() => navigate('/trial?plan=professional&earlyAdopter=true')}>
          Claim jouw plek →
        </button>
      </div>

      {/* ── Pricing cards ── */}
      <div className="pricing-grid">
        {TIERS.map(tier => (
          <div key={tier.id} className={`pricing-card${tier.featured ? ' featured' : ''}`}>
            {tier.featured && <div className="pricing-badge">Meest gekozen</div>}
            <div className="pricing-card-header">
              <div className="pricing-card-name">{tier.name}</div>
              <div className="pricing-card-price">
                <span className="pricing-card-amount">{price(tier)}</span>
                {tier.monthly > 0 && (
                  <span className="pricing-card-period">/maand</span>
                )}
              </div>
              <div className="pricing-card-billed">
                {tier.monthly > 0 && yearly && tier.yearly
                  ? `Jaarlijks gefactureerd (€${tier.yearly * 12}/jaar)`
                  : tier.monthly > 0 ? 'Maandelijks opzegbaar' : ''}
              </div>
              <div className="pricing-card-tagline">{tier.tagline}</div>
            </div>
            <ul className="pricing-card-features">
              {tier.features.map((f, i) => (
                <li key={i}>
                  {f.no
                    ? <span className="pricing-feature-x">✕</span>
                    : <span className="pricing-feature-check">✓</span>
                  }
                  <span style={{ color: f.no ? 'var(--bs-secondary-color)' : undefined, opacity: f.no ? 0.5 : 1 }}>
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>
            <div className="pricing-card-cta">
              <button
                className={
                  tier.ctaStyle === 'primary' ? 'btn-pricing-primary'
                  : tier.ctaStyle === 'ghost'  ? 'btn-pricing-ghost'
                  : 'btn-pricing-outline'
                }
                onClick={() => handleCta(tier)}
              >
                {tier.cta}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Feature comparison table ── */}
      <h2 className="pricing-compare-title">Volledige vergelijking</h2>
      <table className="pricing-compare-table">
        <thead>
          <tr>
            <th style={{ width: '36%' }}>Feature</th>
            <th>Community</th>
            <th>Starter</th>
            <th style={{ color: 'var(--accent, #6366f1)' }}>Professional</th>
            <th>Enterprise</th>
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROWS.map((row, i) => {
            if (row.section) {
              return (
                <tr key={i} className="section-row">
                  <td colSpan={5}>{row.section}</td>
                </tr>
              );
            }
            const cell = (val) => {
              if (val === true)  return <span className="check-yes">✓</span>;
              if (val === false) return <span className="check-no">✕</span>;
              return <span className="check-part">{val}</span>;
            };
            return (
              <tr key={i}>
                <td>{row.label}</td>
                <td>{cell(row.community)}</td>
                <td>{cell(row.starter)}</td>
                <td>{cell(row.pro)}</td>
                <td>{cell(row.ent)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Social proof ── */}
      <h2 className="pricing-compare-title">Wat teams zeggen</h2>
      <div className="pricing-social">
        {[
          {
            avatar: '👩‍💻', name: 'Sarah K.', role: 'CISO · FinTech startup',
            text: '"We moesten aantonen dat we CRA-compliant zijn voor onze Series A. DepGuard gaf ons in een dag een volledig auditrapport. Investeerders waren onder de indruk."',
          },
          {
            avatar: '🧑‍🔧', name: 'Thomas M.', role: 'DevSecOps Lead · E-commerce',
            text: '"De GitHub Actions integratie was binnen 10 minuten actief. Nu krijgen we een Slack-melding bij elke kritieke CVE in onze pipeline. Geen handmatig werk meer."',
          },
          {
            avatar: '👨‍💼', name: 'Erik V.', role: 'CTO · SaaS bedrijf (50 fte)',
            text: '"Snyk was €40K/jaar en deed te veel. DepGuard doet precies wat we nodig hebben voor een fractie van de prijs. De SBOM-diff feature is goud waard."',
          },
        ].map((q, i) => (
          <div key={i} className="pricing-quote">
            <div className="pricing-quote-text">{q.text}</div>
            <div className="pricing-quote-author">
              <div className="pricing-quote-avatar">{q.avatar}</div>
              <div>
                <div className="pricing-quote-name">{q.name}</div>
                <div className="pricing-quote-role">{q.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── FAQ ── */}
      <h2 className="pricing-faq-title">Veelgestelde vragen</h2>
      <div className="pricing-faq">
        {FAQ.map((item, i) => (
          <div key={i} className="pricing-faq-item">
            <button
              className={`pricing-faq-q${openFaq === i ? ' open' : ''}`}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <span>{item.q}</span>
              <span className="faq-arrow">▼</span>
            </button>
            {openFaq === i && (
              <div className="pricing-faq-a">{item.a}</div>
            )}
          </div>
        ))}
      </div>

      {/* ── Bottom CTA ── */}
      <div className="pricing-cta-bottom">
        <h3>Klaar om te beginnen?</h3>
        <p>
          14 dagen gratis proberen — geen creditcard nodig. Of download de Community editie
          en host DepGuard zelf.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn-pricing-primary" style={{ width: 'auto', padding: '12px 32px' }}
            onClick={() => navigate('/trial?plan=professional')}>
            Start 14-dagen trial →
          </button>
          <button className="btn-pricing-outline" style={{ width: 'auto', padding: '12px 28px' }}
            onClick={() => navigate('/docs')}>
            Bekijk documentatie
          </button>
        </div>
      </div>

    </div>
  );
}

// ── Upgrade banner — herbruikbaar in andere views ─────────────────────────────

export function UpgradeBanner({ feature, tier = 'Professional' }) {
  const navigate = useNavigate();
  return (
    <div className="upgrade-banner">
      <div className="upgrade-banner-icon">⚡</div>
      <div className="upgrade-banner-text">
        <strong>{feature} is beschikbaar in {tier}</strong>
        <span>Upgrade je abonnement om deze feature te gebruiken.</span>
      </div>
      <button className="upgrade-banner-btn" onClick={() => navigate('/pricing')}>
        Bekijk plannen
      </button>
    </div>
  );
}
